<script setup lang="ts">
/**
 * BrowserMatrix — 浏览器支持矩阵
 */
import { useI18n } from 'vue-i18n'
import PlatformIcon from '@/components/PlatformIcon.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

const { t } = useI18n()

defineProps<{
  headers: string[]
  platforms: string[]
  rows: string[][]
  headerIcons?: ('chrome' | 'edge' | 'firefox')[]
  platformIcons?: IconName[]
}>()

function cellClass(value: string) {
  if (value === '支持' || value === 'Yes' || value === '対応') return 'matrix__cell--yes'
  if (value === '部分支持' || value === 'Limited' || value === '一部対応') return 'matrix__cell--lim'
  if (value === '不支持' || value === 'Unsupported' || value === '非対応') return 'matrix__cell--no'
  return 'matrix__cell--empty'
}
</script>

<template>
  <div class="matrix">
    <table class="matrix__table">
      <thead>
        <tr>
          <th class="matrix__corner"></th>
          <th v-for="(h, hi) in headers" :key="h" class="matrix__header">
            <div class="matrix__header-icon">
              <PlatformIcon :platform="headerIcons && headerIcons[hi] ? headerIcons[hi] : 'chrome'" :size="24" />
            </div>
            <span>{{ h }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(platform, idx) in platforms" :key="platform">
          <th class="matrix__platform">
            <YfbIcon v-if="platformIcons && platformIcons[idx]" :name="platformIcons[idx]" :size="16" />
            <span>{{ platform }}</span>
          </th>
          <td v-for="(cell, j) in rows[idx]" :key="j" :class="['matrix__cell', cellClass(cell)]">
            <span v-if="cellClass(cell) !== 'matrix__cell--empty'">{{ cell }}</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="matrix__legend">
      <span class="matrix__legend-item matrix__cell--yes">▓ {{ t('home.matrix.legend.yes') }}</span>
      <span class="matrix__legend-item matrix__cell--lim">▒ {{ t('home.matrix.legend.limited') }}</span>
      <span class="matrix__legend-item matrix__cell--no">✕ {{ t('home.matrix.legend.no') }}</span>
    </div>
  </div>
</template>

<style scoped>
.matrix {
  width: 100%;
  overflow-x: auto;
}

.matrix__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  background-color: var(--yfb-surface);
  overflow: hidden;
}

.matrix__corner {
  width: 40px;
  padding: var(--yfb-sp-3);
  border-bottom: 1px solid var(--yfb-rule-soft);
  border-right: 1px solid var(--yfb-rule-soft);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink-soft);
}

.matrix__header {
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  border-bottom: 1px solid var(--yfb-rule-soft);
  border-right: 1px solid var(--yfb-rule-soft);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  font-family: var(--yfb-font-heading);
  font-weight: 700;
  font-size: var(--yfb-fs-sm);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

.matrix__header-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 6px;
}

.matrix__platform {
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  border-bottom: 1px solid var(--yfb-rule-soft);
  border-right: 1px solid var(--yfb-rule-soft);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  font-weight: 600;
  font-size: var(--yfb-fs-sm);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}

.matrix__cell {
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  border-bottom: 1px solid var(--yfb-rule-soft);
  border-right: 1px solid var(--yfb-rule-soft);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  text-align: center;
  font-family: var(--yfb-mono);
  letter-spacing: var(--yfb-ls-mono);
  vertical-align: middle;
}

.matrix__cell--yes {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.matrix__cell--lim {
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
}

.matrix__cell--no {
  background-color: var(--yfb-signal-danger);
  color: var(--yfb-on-danger);
}

.matrix__cell--empty {
  background-color: var(--yfb-surface);
  color: var(--yfb-rule-soft);
}

.matrix__legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--yfb-sp-2) var(--yfb-sp-4);
  margin-top: 16px;
  font-size: var(--yfb-fs-xs);
  font-weight: 600;
  font-family: var(--yfb-mono);
  text-transform: uppercase;
}

.matrix__legend-item {
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

.matrix__legend-item.matrix__cell--yes {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.matrix__legend-item.matrix__cell--lim {
  background-color: var(--yfb-surface-2);
}

.matrix__legend-item.matrix__cell--no {
  background-color: var(--yfb-signal-danger);
  color: var(--yfb-on-danger);
}

.matrix__legend-item.matrix__cell--empty {
  background-color: var(--yfb-surface);
}

@media (max-width: 767px) {
  .matrix__header,
  .matrix__platform,
  .matrix__cell {
    padding: var(--yfb-sp-2) var(--yfb-sp-1);
    font-size: 10px;
  }
  .matrix__corner {
    padding: var(--yfb-sp-2);
    width: 32px;
  }
  .matrix__header-icon {
    margin-bottom: 4px;
  }
}
</style>